import {
  Alert,
  Box,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material'
import {
  ArchetypeResponseDto,
  CarrierType,
  CompanyType,
  RailVisitResponseDto,
  VesselVisitDto,
} from '@planning/app/api'
import { usePlanningStore } from '@planning/AppProvider'
import { CompanyAutocomplete } from '@planning/components/autocompletes/company-autocomplete/CompanyAutocomplete'
import { CustomDatePicker } from '@planning/components/CustomDatePicker'
import { RailVisitAutoComplete } from '@planning/components/visit/RailVisitAutoComplete'
import { VesselVisitAutoComplete } from '@planning/components/visit/VesselVisitAutoComplete'
import { carrierVisitTypes } from '@planning/constants'
import { railVisitService, vesselVisitService } from '@planning/services'
import { isValidityExpiredByNow } from '@planning/utils/release-validity-utils'
import { useTranslate } from '@tolgee/react'
import { RequiredField } from '@tom-ui/ui'
import { useAsyncFetch } from '@tom-ui/utils'
import dayjs from 'dayjs'
import { observer } from 'mobx-react-lite'
import { FC, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import {
  NnrOrderControlViewStore,
  NnrOrderFormData,
} from '../../../Stores/NnrOrderControlViewStore'
import { ControlledArchetypeInputWithDetails } from '../ControlledArchetypeInputWithDetails/ControlledArchetypeInputWithDetails'

interface Props {
  viewStore: NnrOrderControlViewStore
  onSubmit: (data: NnrOrderFormData) => Promise<void>
}

export const NonNumericReleaseForm: FC<Props> = observer(({ viewStore, onSubmit }) => {
  const { isoCodeMappingInputStore } = usePlanningStore()
  const { t } = useTranslate()

  const form = useForm<NnrOrderFormData>({
    defaultValues: {
      archetypeId: undefined,
      referenceNumber: undefined,
      shippingLine: undefined,
      amount: undefined,
      modeOfTransport: undefined,
      transportCompanyId: undefined,
      carrierVisitId: undefined,
      containerIsoCode: undefined,
      validityDate: undefined,
    },
  })

  const {
    control,
    register,
    getValues,
    reset,
    watch,
    handleSubmit,
    setError,
    clearErrors,
    formState,
  } = form
  const { errors } = formState

  const [selectedArchetype, _] = useState<ArchetypeResponseDto | undefined>(
    viewStore.selectedArchetype,
  )

  // todo: [north] will replace once there is vesselItemStore
  const [selectedVisit, setSelectedVisit] = useState<VesselVisitDto | RailVisitResponseDto | null>(
    null,
  )

  useAsyncFetch(async () => {
    if (viewStore.selectedNnrOrder) {
      const {
        archetypeId,
        referenceNumber,
        shippingLine,
        amount,
        modeOfTransport,
        transportCompanyId,
        carrierVisitId,
        containerIsoCode,
        validityDate,
      } = viewStore.selectedNnrOrder.data

      reset({
        archetypeId,
        referenceNumber,
        shippingLine,
        amount,
        modeOfTransport,
        transportCompanyId,
        carrierVisitId,
        containerIsoCode,
        validityDate: validityDate ? dayjs(validityDate) : undefined,
      })

      if (carrierVisitId) {
        let visit = null
        if (modeOfTransport === CarrierType.Train) {
          visit = await railVisitService.getById(carrierVisitId)
        } else if (modeOfTransport === CarrierType.Vessel) {
          visit = await vesselVisitService.getById(carrierVisitId)
        }

        setSelectedVisit(visit)
      }
    }
  }, [reset, viewStore.selectedNnrOrder])

  // [Review] TODO: Why all the state here? Would be better to use FormStores.
  const watchModeOfTransport = watch('modeOfTransport')

  const [isIsoCodeToggled, setIsIsoCodeToggled] = useState(false)

  const isValidityDateValid = (date: string | number | Date | dayjs.Dayjs | null | undefined) => {
    // Temporary workaround: validate by exact timestamp (UTC flow), not calendar-day boundary.
    const isBeforeNow = isValidityExpiredByNow(date)

    if (isBeforeNow) {
      setError('validityDate', {
        type: 'datePast',
        message: t('pleaseSelectAFutureDate', 'Please select a future date'),
      })
    } else {
      clearErrors('validityDate')
    }

    return !isBeforeNow
  }

  const submit = async (data: NnrOrderFormData) => {
    let isValid = true
    if (data.validityDate) {
      isValid = isValidityDateValid(data.validityDate)
    }

    if (isValid) {
      await onSubmit(data)
    }
  }

  const canEdit = useMemo(
    () => (viewStore.selectedNnrOrder ? viewStore.selectedNnrOrder.canEdit : true),
    [viewStore.selectedNnrOrder],
  )

  const isInProgress = useMemo(
    () => (viewStore.selectedNnrOrder ? viewStore.selectedNnrOrder.assigned > 0 : false),
    [viewStore.selectedNnrOrder],
  )

  return (
    <form id='nnr-order-form' onSubmit={handleSubmit(submit)} autoComplete='off' noValidate>
      <Grid container spacing={2}>
        <Grid size={6}>
          <Controller
            control={control}
            name='shippingLine'
            rules={{ required: true }}
            render={({ field }) => (
              <CompanyAutocomplete
                disabled={!canEdit || isInProgress}
                label={t('operator', 'Operator')}
                selectedCompanyShortName={field.value}
                handleOnChange={operator => {
                  field.onChange(operator?.shortName)
                }}
                filterByTypes={[CompanyType.ShippingLine]}
                dataCy='operator-autocomplete'
                required
                error={!!errors.shippingLine}
                helperText={errors.shippingLine ? 'Field is required.' : ''}
              />
            )}
          />
        </Grid>
        <Grid size={6}>
          <Controller
            control={control}
            name='referenceNumber'
            rules={{ required: true }}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                required
                disabled={isInProgress}
                label={t('referenceNumber', 'Reference Number')}
                onChange={e => {
                  field.onChange(e.target.value.toUpperCase())
                }}
                variant='outlined'
                data-cy='create-nnr-form-reference'
                error={!!errors.referenceNumber}
                helperText={errors.referenceNumber ? 'Field is required.' : ''}
              />
            )}
          />
        </Grid>

        <Grid size={6}>
          <Controller
            control={control}
            name='validityDate'
            render={({ field, fieldState }) => (
              <CustomDatePicker
                {...field}
                value={field.value ?? null}
                label={t('validityDate', 'Validity Date')}
                onChange={e => {
                  const d = e ? dayjs(e).endOf('day') : null
                  isValidityDateValid(d)
                  field.onChange(d)
                }}
                disablePast
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>
      </Grid>
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography variant='h4' sx={{ marginTop: '1rem' }}>
            {t('modeOfTransport', 'Mode of transport')}
          </Typography>
        </Grid>
        <Grid size={6}>
          <FormControl fullWidth>
            <InputLabel id='mode-of-transport-label' sx={{ verticalAlign: 'center' }}>
              {t('modeOfTransport', 'Mode of transport')} <RequiredField />
            </InputLabel>
            <Controller
              control={control}
              name={`modeOfTransport`}
              rules={{ required: true }}
              render={({ field: { onChange } }) => (
                <Select
                  disabled={!canEdit || isInProgress}
                  labelId='mode-of-transport-label'
                  required
                  value={getValues(`modeOfTransport`) ?? ''}
                  onChange={onChange}
                  data-cy='create-nnr-form-transport'
                  error={!!errors.modeOfTransport}
                >
                  {carrierVisitTypes.map(d => {
                    return (
                      <MenuItem key={d.key} value={d.name}>
                        {d.name}
                      </MenuItem>
                    )
                  })}
                </Select>
              )}
            />
            {errors.modeOfTransport && (
              <FormHelperText error>{t('fieldIsRequired', 'Field is required.')}</FormHelperText>
            )}
          </FormControl>
        </Grid>
        <Grid size={6}>
          {watchModeOfTransport === CarrierType.Vessel && (
            <Box>
              <Controller
                control={control}
                name={`carrierVisitId`}
                rules={{ required: true }}
                render={({ field: { onChange } }) => (
                  <VesselVisitAutoComplete
                    disabled={!canEdit || isInProgress}
                    required
                    dataCy='vessel-autocomplete'
                    selectedVesselVisit={
                      selectedVisit ? (selectedVisit as VesselVisitDto) : undefined
                    }
                    handleOnChange={visit => onChange(visit?.id)}
                  />
                )}
              />
            </Box>
          )}
          {watchModeOfTransport === CarrierType.Train && (
            <Box>
              <Controller
                control={control}
                name={`carrierVisitId`}
                rules={{ required: true }}
                render={({ field: { onChange } }) => (
                  <RailVisitAutoComplete
                    disabled={!canEdit || isInProgress}
                    required
                    selectedRailVisit={selectedVisit as RailVisitResponseDto}
                    handleOnChange={visit => {
                      if (visit) {
                        onChange(visit.id)
                        setSelectedVisit(visit)
                      }
                    }}
                    error={!!errors.carrierVisitId}
                    helperText={
                      errors.carrierVisitId ? t('fieldIsRequired', 'Field is required.') : ''
                    }
                    cargoType='Container'
                  />
                )}
              />
            </Box>
          )}
          {watchModeOfTransport === CarrierType.Truck && (
            <Box>
              <Controller
                control={control}
                name={`transportCompanyId`}
                render={({ field: { onChange } }) => (
                  <CompanyAutocomplete
                    label={t('transportCompany', 'Transport Company')}
                    filterByTypes={[CompanyType.TransportCompany]}
                    selectedCompanyId={viewStore.selectedNnrOrder?.data.transportCompanyId}
                    disabled={!canEdit || isInProgress}
                    handleOnChange={company => {
                      if (company) {
                        onChange(company.id)
                      }
                    }}
                    error={!!errors.carrierVisitId}
                    helperText={
                      errors.carrierVisitId ? t('fieldIsRequired', 'Field is required.') : ''
                    }
                    dataCy='transport-company-autocomplete'
                  />
                )}
              />
            </Box>
          )}
        </Grid>
      </Grid>
      <Typography variant='h4' sx={{ marginTop: '1rem' }}>
        {t('containerRequirements', 'Container requirements')}
      </Typography>
      <Chip
        label={t('archetype', 'Archetype')}
        sx={{ m: '1rem 1rem 1rem 0' }}
        variant={isIsoCodeToggled ? 'outlined' : undefined}
        data-cy='create-nnr-form-toggle-containerAttributes'
        onClick={() => setIsIsoCodeToggled(false)}
        disabled={isInProgress}
      />
      <Chip
        label={t('isoCode', 'ISO Code')}
        sx={{ mt: '1rem', mb: '1rem' }}
        variant={!isIsoCodeToggled ? 'outlined' : undefined}
        data-cy='create-nnr-form-toggle-isocode'
        onClick={() => setIsIsoCodeToggled(true)}
        disabled={isInProgress}
      />
      <Grid container>
        <Grid size={12} gap={2}>
          {isIsoCodeToggled ? (
            <>
              <Alert sx={{ mb: 2 }} severity='warning'>
                <Typography variant='subtitle1'>
                  {t('isoCodeMatchingLimitation', 'ISO Code matching limitation')}
                </Typography>
                <Typography variant='body1'>
                  {t(
                    'systemWillAlwaysMatchContainersToTheArchetypeNotTheExactISOCode',
                    'The system matches containers to their archetype, not the exact ISO code!',
                  )}
                </Typography>
              </Alert>
              {/* [Review] TODO: Stores should concerned with state and state manipulation, not to render components.*/}
              {/* The same can be achieved by just passing a function from the host module. */}
              {isoCodeMappingInputStore.renderIsoCodeMappingInput?.({
                label: `${t('isoCode', 'ISO Code')}`,
                fullWidth: true,
                variant: 'outlined',
                type: 'text',
                uppercase: true,
                formState: formState,
                name: 'containerIsoCode',
                required: true,
                control: control,
                showArchetype: true,
                showArchetypeAttributes: true,
                inputSize: 2,
                disabled: !canEdit || isInProgress,
                setValue: (value: string) => form.setValue('containerIsoCode', value),
                watch: watch,
                onChange: archetypeIsoCodeCharacteristics => {
                  form.setValue('archetypeId', archetypeIsoCodeCharacteristics?.archetype.id ?? -1)
                },
              })}
            </>
          ) : (
            <ControlledArchetypeInputWithDetails
              name='archetypeId'
              required={true}
              arquetype={selectedArchetype}
              control={control}
              formState={formState}
              inputSize={2}
              disabled={!canEdit || isInProgress}
            />
          )}
        </Grid>
        <Grid container spacing={1}>
          <Grid>
            <TextField
              fullWidth
              required
              label={t('quantity', 'Quantity')}
              data-cy='create-nnr-form-amount'
              variant='outlined'
              type='number'
              {...register('amount', {
                required: true,
                valueAsNumber: true,
                min: 1,
                validate: {
                  amountLessThanAssigned: value => {
                    if (viewStore.selectedNnrOrder && value < viewStore.selectedNnrOrder.assigned) {
                      return false
                    }
                    return true
                  },
                },
              })}
              name='amount'
              error={!!errors.amount}
              helperText={
                <>
                  {errors.amount?.type === 'min' &&
                    t('quantityMustBeAtLeastOne', 'Quantity must be at least 1.')}
                  {errors.amount?.type === 'amountLessThanAssigned' &&
                    t(
                      'assignedQuantityExceedsEnteredQuantity',
                      'Assigned quantity exceeds entered quantity.',
                    )}
                  {errors.amount?.type === 'required' && t('fieldIsRequired', 'Field is required.')}
                </>
              }
              sx={{ marginTop: '1rem' }}
              onWheel={event => event.target instanceof HTMLElement && event.target.blur()}
              inputProps={{ min: viewStore.selectedNnrOrder?.fulfilled ?? 1 }}
            />
          </Grid>
        </Grid>
      </Grid>
    </form>
  )
})
